import { useMemo } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import brandOverview from "@/data/brandOverview.json";

const TONE = {
  Trusted: "neutral",
  Supervised: "amber",
  Beta: "green",
  Autonomous: "green",
};

const BRAND_PAGES = {
  at: [
    { label: "Overview", path: "/at" },
    { label: "Email", path: "/at/email" },
    { label: "Calendar", path: "/at/calendar" },
    { label: "People", path: "/at/people" },
  ],
  tjb: [
    { label: "Overview", path: "/tjb" },
    { label: "Cities", path: "/tjb/cities" },
    { label: "Email", path: "/tjb/email" },
    { label: "Calendar", path: "/tjb/calendar" },
    { label: "People", path: "/tjb/people" },
  ],
  to: [
    { label: "Overview", path: "/to" },
    { label: "Email", path: "/to/email" },
    { label: "Calendar", path: "/to/calendar" },
    { label: "People", path: "/to/people" },
  ],
  av: [
    { label: "Overview", path: "/av" },
    { label: "Email", path: "/av/email" },
    { label: "Calendar", path: "/av/calendar" },
    { label: "People", path: "/av/people" },
  ],
  wp: [
    { label: "Overview", path: "/wp" },
    { label: "Email", path: "/wp/email" },
    { label: "Calendar", path: "/wp/calendar" },
    { label: "People", path: "/wp/people" },
  ],
  tm: [
    { label: "Overview", path: "/tm" },
    { label: "Email", path: "/tm/email" },
    { label: "Calendar", path: "/tm/calendar" },
    { label: "People", path: "/tm/people" },
  ],
  sv: [
    { label: "Overview", path: "/sv" },
    { label: "Email", path: "/sv/email" },
    { label: "Calendar", path: "/sv/calendar" },
    { label: "People", path: "/sv/people" },
  ],
  sd: [
    { label: "Overview", path: "/sd" },
    { label: "Email", path: "/sd/email" },
    { label: "Calendar", path: "/sd/calendar" },
    { label: "People", path: "/sd/people" },
  ],
};

export default function BrandOverviewPage({ brandKey }) {
  const desktop = useMediaQuery("(min-width: 1024px)");

  const card = useMemo(() => {
    const slug = `brand-overview:${brandKey}`;
    return brandOverview.cards.find((c) => c.slug === slug) || null;
  }, [brandKey]);

  const pages = BRAND_PAGES[brandKey] || [];
  const p = card?.payload;
  const tone = TONE[p?.stage] || "neutral";

  return (
    <div data-testid={`brand-overview-${brandKey}`} className="w-full font-inter text-os-ink">
      <div className={cn(desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4")}>
        {/* Header card */}
        <Card as="section" className={cn("flex flex-col gap-2", desktop ? "px-7 py-5" : "px-5 py-4")}>
          <div className="flex items-baseline justify-between gap-3">
            <h1 className="min-w-0 text-[1.5rem] font-semibold tracking-[-0.02em] text-os-ink">
              {p?.displayName || "—"}
            </h1>
            <StateWord tone={tone}>{p?.stageWord || "—"}</StateWord>
          </div>
          <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
            {p?.stage ? `${p.stage} — overview` : "—"}
          </p>
          <MonoMeta as="p" className="pt-1">
            North Star: {p?.northStar || "—"}
          </MonoMeta>
        </Card>

        {/* Quick State */}
        <section className="flex flex-col gap-3">
          <Label as="h2">Quick State</Label>
          <Card quiet className={cn("flex flex-col gap-2", desktop ? "px-7 py-5" : "px-5 py-4")}>
            {p?.quickState?.length ? (
              <ul className="flex flex-col gap-2">
                {p.quickState.map((b, i) => (
                  <li key={i} className="text-[0.875rem] leading-[1.45] text-os-ink-2">
                    {b}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[0.875rem] leading-[1.45] text-os-ink-3">—</p>
            )}
          </Card>
        </section>

        {/* Metrics */}
        <section className="flex flex-col gap-3">
          <Label as="h2">Metrics</Label>
          <Card quiet className={cn("flex flex-col gap-2", desktop ? "px-7 py-5" : "px-5 py-4")}>
            {p?.metrics?.length ? (
              <ul className="flex flex-col gap-2">
                {p.metrics.map((m, i) => (
                  <li key={i} className="text-[0.875rem] leading-[1.45] text-os-ink-2">
                    {m}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[0.875rem] leading-[1.45] text-os-ink-3">—</p>
            )}
          </Card>
        </section>

        {/* Admin Links */}
        <section className="flex flex-col gap-3">
          <Label as="h2">Admin Links</Label>
          <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
            {p?.adminLinks?.length ? (
              p.adminLinks.map((link, i) =>
                link.url ? (
                  <Card
                    key={i}
                    className="cursor-pointer px-5 py-3 transition-colors hover:border-os-border-strong"
                    as="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="text-[0.875rem] font-medium text-os-ink">{link.label}</span>
                  </Card>
                ) : (
                  <Card key={i} quiet className="flex items-center justify-between px-5 py-3">
                    <span className="text-[0.875rem] text-os-ink-2">{link.label}</span>
                    <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">offline</span>
                  </Card>
                ),
              )
            ) : (
              <p className="text-[0.875rem] leading-[1.45] text-os-ink-3">—</p>
            )}
          </div>
        </section>

        {/* Canonical Files */}
        <section className="flex flex-col gap-3">
          <Label as="h2">Canonical Files</Label>
          <Card quiet className={cn("flex flex-col gap-2", desktop ? "px-7 py-5" : "px-5 py-4")}>
            {p?.canonicalFiles?.length ? (
              <ul className="flex flex-col gap-2">
                {p.canonicalFiles.map((f, i) => (
                  <li key={i} className="flex items-baseline gap-2 text-[0.875rem] leading-[1.45] text-os-ink-2">
                    <span className="font-mono text-[0.75rem] text-os-ink-3">{f.file}</span>
                    <span className="text-os-ink-4">·</span>
                    <span>{f.owns}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[0.875rem] leading-[1.45] text-os-ink-3">—</p>
            )}
          </Card>
        </section>

        {/* Pages */}
        <section className="flex flex-col gap-3">
          <Label as="h2">Pages</Label>
          <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
            {pages.map((p) =>
              p.soon ? (
                <Card key={p.label} quiet className="flex items-center justify-between px-5 py-3">
                  <span className="text-[0.875rem] text-os-ink-2">{p.label}</span>
                  <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">soon</span>
                </Card>
              ) : (
                <Card
                  key={p.label}
                  className="cursor-pointer px-5 py-3 transition-colors hover:border-os-border-strong"
                  as={Link}
                  to={p.path}
                >
                  <span className="text-[0.875rem] font-medium text-os-ink">{p.label}</span>
                </Card>
              ),
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
